import { Routes } from '@angular/router';
import { COMPLIANCE_CONTROL_MANAGE_PERMISSIONS, PERMISSIONS } from '@ewentscompany/ewents-settings';
import { AuthGuard } from './shared/guards/auth.guard';
import { LoginGuard } from './shared/guards/login.guard';
import { PermissionGuard } from './shared/guards/permission.guard';
import { HUB_DASHBOARD_PERMISSIONS } from './shared/constants/hub-dashboard.permissions';
import { LayoutComponent } from './layout/layout.component';
import { SETTINGS_GROUPS, settingsGroupPermissions } from './settings/settings-nav';

/** Profile tabs shared by Team Member and Candidate (both render Contact detail). */
function personProfileTabRoutes(): Routes {
  return [
    {
      path: 'assignments',
      canActivate: [AuthGuard, PermissionGuard],
      data: {
        permissions: [
          PERMISSIONS.ASSIGNMENTS.LIST,
          PERMISSIONS.ASSIGNMENTS.MANAGER,
          PERMISSIONS.GLOBAL.ADMIN,
        ],
      },
      loadComponent: () =>
        import('./talents/tabs/talent-assignments-tab/talent-assignments-tab.component').then(
          m => m.TalentAssignmentsTabComponent
        ),
    },
    {
      path: 'technologies',
      data: { catalog: 'technologies' },
      loadComponent: () =>
        import('./contacts/profile-skills-tab/profile-skills-tab.component').then(
          m => m.ProfileSkillsTabComponent
        ),
    },
    {
      path: 'specialities',
      data: { catalog: 'specialities' },
      loadComponent: () =>
        import('./contacts/profile-skills-tab/profile-skills-tab.component').then(
          m => m.ProfileSkillsTabComponent
        ),
    },
    {
      path: 'cv',
      loadComponent: () =>
        import('./contacts/profile-cv-tab/profile-cv-tab.component').then(
          m => m.ProfileCvTabComponent
        ),
    },
    {
      path: 'timesheets',
      loadComponent: () =>
        import('./talents/tabs/talent-timesheets-tab/talent-timesheets-tab.component').then(
          m => m.TalentTimesheetsTabComponent
        ),
    },
    {
      path: 'documents',
      loadComponent: () =>
        import('./talents/tabs/talent-work-orders-tab/talent-work-orders-tab.component').then(
          m => m.TalentWorkOrdersTabComponent
        ),
    },
    {
      path: 'job-descriptions',
      canActivate: [AuthGuard, PermissionGuard],
      data: {
        permissions: [PERMISSIONS.HR.LIST, PERMISSIONS.HR.MANAGER, PERMISSIONS.GLOBAL.ADMIN],
      },
      loadComponent: () =>
        import('./contacts/job-descriptions-tab/job-descriptions-tab.component').then(
          m => m.JobDescriptionsTabComponent
        ),
    },
    {
      path: 'interviews',
      canActivate: [AuthGuard, PermissionGuard],
      data: {
        permissions: [
          PERMISSIONS.HR.LIST,
          PERMISSIONS.HR.MANAGER,
          PERMISSIONS.TALENTS.LIST,
          PERMISSIONS.TALENTS.MANAGER,
          PERMISSIONS.GLOBAL.ADMIN,
        ],
      },
      loadComponent: () =>
        import('./candidates/interview-feedback-tab/interview-feedback-tab.component').then(
          m => m.InterviewFeedbackTabComponent
        ),
    },
  ];
}

export const routes: Routes = [
  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        title: 'Dashboard',
        canActivate: [AuthGuard, PermissionGuard],
        data: { permissions: [...HUB_DASHBOARD_PERMISSIONS] },
        loadComponent: () =>
          import('./dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'finance/overview',
        title: 'Finance Overview',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.FINANCIAL.VIEW_REVENUE,
            PERMISSIONS.FINANCIAL.VIEW_REPORTS,
            PERMISSIONS.FINANCIAL.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./finance/finance-overview/finance-overview.component').then(
            m => m.FinanceOverviewComponent
          ),
      },
      {
        path: 'projects',
        title: 'Projects',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.OPERATIONS.PROJECTS.LIST,
            PERMISSIONS.OPERATIONS.PROJECTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./projects/project-list/project-list.component').then(
            m => m.ProjectListComponent
          ),
      },
      {
        path: 'projects/:id',
        title: 'Project',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.OPERATIONS.PROJECTS.LIST,
            PERMISSIONS.OPERATIONS.PROJECTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./projects/project-detail/project-detail.component').then(
            m => m.ProjectDetailComponent
          ),
        children: [
          {
            path: 'assignments',
            loadComponent: () =>
              import('./projects/project-detail/project-detail-tab-outlet.component').then(
                m => m.ProjectDetailTabOutletComponent
              ),
          },
          {
            path: 'milestones',
            loadComponent: () =>
              import('./projects/project-detail/project-detail-tab-outlet.component').then(
                m => m.ProjectDetailTabOutletComponent
              ),
          },
          {
            path: 'contract',
            loadComponent: () =>
              import('./projects/project-detail/project-detail-tab-outlet.component').then(
                m => m.ProjectDetailTabOutletComponent
              ),
          },
        ],
      },
      {
        // Legacy standalone page — send to the project-detail Milestones tab.
        path: 'projects/:projectId/milestone-management',
        redirectTo: '/projects/:projectId/milestones',
        pathMatch: 'full',
      },
      {
        path: 'vendors',
        title: 'Vendors',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.VENDORS.LIST,
            PERMISSIONS.VENDORS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./vendors/vendor-list/vendor-list.component').then(m => m.VendorListComponent),
      },
      {
        path: 'marketing/posts',
        title: 'Blog posts',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.MARKETING.LIST,
            PERMISSIONS.MARKETING.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./marketing/blog-post-list/blog-post-list.component').then(
            m => m.BlogPostListComponent
          ),
      },
      {
        path: 'marketing/stats',
        title: 'Website analytics',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.MARKETING.LIST,
            PERMISSIONS.MARKETING.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./marketing/marketing-stats/marketing-stats.component').then(
            m => m.MarketingStatsComponent
          ),
      },
      {
        path: 'marketing/stats/journey',
        title: 'Journey forms',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.MARKETING.LIST,
            PERMISSIONS.MARKETING.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./marketing/marketing-stats/marketing-journey-detail.component').then(
            m => m.MarketingJourneyDetailComponent
          ),
      },
      {
        path: 'marketing/linkedin-activity',
        title: 'LinkedIn activity',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.LEAD_GENERATORS.LIST,
            PERMISSIONS.LEAD_GENERATORS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./marketing/linkedin-activity/linkedin-activity.component').then(
            m => m.LinkedInActivityComponent
          ),
      },
      {
        path: 'marketing/instantly/visitors/:id',
        title: 'Instantly visitor',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.MARKETING.LIST,
            PERMISSIONS.MARKETING.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./marketing/instantly-outreach/instantly-visitor-detail.component').then(
            m => m.InstantlyVisitorDetailComponent
          ),
      },
      {
        path: 'marketing/instantly',
        title: 'Instantly visitors',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.MARKETING.LIST,
            PERMISSIONS.MARKETING.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./marketing/instantly-outreach/instantly-outreach.component').then(
            m => m.InstantlyOutreachComponent
          ),
        children: [
          {
            path: 'emails',
            loadComponent: () =>
              import('./marketing/instantly-outreach/instantly-outreach-tab-outlet.component').then(
                m => m.InstantlyOutreachTabOutletComponent
              ),
          },
          {
            path: 'templates',
            loadComponent: () =>
              import('./marketing/instantly-outreach/instantly-outreach-tab-outlet.component').then(
                m => m.InstantlyOutreachTabOutletComponent
              ),
          },
        ],
      },
      {
        path: 'marketing/posts/:id',
        title: 'Blog post',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.MARKETING.LIST,
            PERMISSIONS.MARKETING.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./marketing/blog-post-editor/blog-post-editor.component').then(
            m => m.BlogPostEditorComponent
          ),
      },
      {
        path: 'vendors/:id',
        title: 'Vendor',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.VENDORS.LIST,
            PERMISSIONS.VENDORS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./vendors/vendor-detail/vendor-detail.component').then(
            m => m.VendorDetailComponent
          ),
        children: [
          {
            path: 'contracts',
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permissions: [
                PERMISSIONS.CONTRACTS.LIST,
                PERMISSIONS.CONTRACTS.MANAGER,
                PERMISSIONS.GLOBAL.ADMIN,
              ],
            },
            loadComponent: () =>
              import('./vendors/tabs/vendor-contracts-tab/vendor-contracts-tab.component').then(
                m => m.VendorContractsTabComponent
              ),
          },
          {
            path: 'contacts',
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permissions: [
                PERMISSIONS.VENDORS.LIST,
                PERMISSIONS.VENDORS.MANAGER,
                PERMISSIONS.GLOBAL.ADMIN,
              ],
            },
            loadComponent: () =>
              import('./vendors/tabs/vendor-contacts-tab/vendor-contacts-tab.component').then(
                m => m.VendorContactsTabComponent
              ),
          },
        ],
      },
      {
        // ponytail: Candidates/Recruiters reuse TALENTS.* intentionally (no CANDIDATES/RECRUITERS perms).
        path: 'candidates',
        title: 'Candidates',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./candidates/candidate-list/candidate-list.component').then(
            m => m.CandidateListComponent
          ),
      },
      {
        path: 'candidates/:id/ewents-cv',
        title: 'Ewents CV',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.UPDATE,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./candidates/candidate-ewents-cv/candidate-ewents-cv.component').then(
            m => m.CandidateEwentsCvComponent
          ),
      },
      {
        path: 'candidates/:id',
        title: 'Candidate',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./contacts/contact-detail/contact-detail.component').then(
            m => m.ContactDetailComponent
          ),
        children: personProfileTabRoutes(),
      },
      {
        path: 'team-members',
        title: 'Team Members',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          // Team Members is the Talent module. Contacts keys do not open it.
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./team-members/team-member-list/team-member-list.component').then(
            m => m.TeamMemberListComponent
          ),
      },
      {
        path: 'team-members/:id',
        title: 'Team Member',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        // Team Member and Candidate share this page. INTERNAL activates the
        // talent tabs; Interview feedback is the extra child both surfaces use.
        loadComponent: () =>
          import('./contacts/contact-detail/contact-detail.component').then(
            m => m.ContactDetailComponent
          ),
        children: personProfileTabRoutes(),
      },
      {
        path: 'sales/performance',
        title: 'Performance',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CLIENTS.LIST,
            PERMISSIONS.CLIENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./sales/sales-performance/sales-performance.component').then(
            m => m.SalesPerformanceComponent
          ),
      },
      {
        path: 'leads',
        title: 'Leads',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CLIENTS.LIST,
            PERMISSIONS.CLIENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./leads/lead-list/lead-list.component').then(m => m.LeadListComponent),
      },
      {
        path: 'leads/:id',
        title: 'Lead',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CLIENTS.LIST,
            PERMISSIONS.CLIENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./leads/lead-detail/lead-detail.component').then(m => m.LeadDetailComponent),
      },
      {
        path: 'deals',
        title: 'Deals',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CLIENTS.LIST,
            PERMISSIONS.CLIENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadChildren: () => import('./deals/deals.routes').then(m => m.default),
      },
      {
        path: 'clients',
        title: 'Clients',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CLIENTS.LIST,
            PERMISSIONS.CLIENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./clients/client-list/client-list.component').then(m => m.ClientListComponent),
      },
      {
        path: 'clients/:id',
        title: 'Client',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CLIENTS.LIST,
            PERMISSIONS.CLIENTS.MANAGER,
            PERMISSIONS.CLIENTS.VIEW_PROCESSES,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./clients/client-detail/client-detail.component').then(
            m => m.ClientDetailComponent
          ),
        children: [
          {
            path: 'processes',
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permissions: [
                PERMISSIONS.CLIENTS.VIEW_PROCESSES,
                PERMISSIONS.CLIENTS.MANAGER,
                PERMISSIONS.GLOBAL.ADMIN,
              ],
            },
            loadComponent: () =>
              import('./clients/tabs/client-processes-tab/client-processes-tab.component').then(
                m => m.ClientProcessesTabComponent
              ),
          },
          {
            path: 'talents-process',
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permissions: [
                PERMISSIONS.CLIENTS.VIEW_PROCESSES,
                PERMISSIONS.CLIENTS.MANAGER,
                PERMISSIONS.GLOBAL.ADMIN,
              ],
            },
            loadComponent: () =>
              import('./clients/tabs/client-talents-process-tab/client-talents-process-tab.component').then(
                m => m.ClientResourcesProcessTabComponent
              ),
          },
          {
            path: 'contracts',
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permissions: [
                PERMISSIONS.CONTRACTS.LIST,
                PERMISSIONS.CONTRACTS.MANAGER,
                PERMISSIONS.GLOBAL.ADMIN,
              ],
            },
            loadComponent: () =>
              import('./clients/tabs/client-contracts-tab/client-contracts-tab.component').then(
                m => m.ClientContractsTabComponent
              ),
          },
          {
            path: 'projects',
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permissions: [
                PERMISSIONS.OPERATIONS.PROJECTS.LIST,
                PERMISSIONS.OPERATIONS.PROJECTS.MANAGER,
                PERMISSIONS.GLOBAL.ADMIN,
              ],
            },
            loadComponent: () =>
              import('./clients/tabs/client-projects-tab/client-projects-tab.component').then(
                m => m.ClientProjectsTabComponent
              ),
          },
          {
            path: 'assignments',
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permissions: [
                PERMISSIONS.ASSIGNMENTS.LIST,
                PERMISSIONS.ASSIGNMENTS.MANAGER,
                PERMISSIONS.GLOBAL.ADMIN,
              ],
            },
            loadComponent: () =>
              import('./clients/tabs/client-assignments-tab/client-assignments-tab.component').then(
                m => m.ClientAssignmentsTabComponent
              ),
          },
          {
            path: 'contacts',
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permissions: [
                PERMISSIONS.CLIENTS.LIST,
                PERMISSIONS.CLIENTS.MANAGER,
                PERMISSIONS.GLOBAL.ADMIN,
              ],
            },
            loadComponent: () =>
              import('./clients/tabs/client-contacts-tab/client-contacts-tab.component').then(
                m => m.ClientContactsTabComponent
              ),
          },
        ],
      },
      {
        path: 'clients/:clientId/talents-process/:processId',
        title: 'Talent Process',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CLIENTS.VIEW_PROCESSES,
            PERMISSIONS.CLIENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./clients/client-talent-process-detail/client-talent-process-detail.component').then(
            m => m.ClientTalentProcessDetailComponent
          ),
      },
      // Talent process detail route
      {
        path: 'talents/:talentId/processes/:processId',
        title: 'Talent Process',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./clients/client-talent-process-detail/client-talent-process-detail.component').then(
            m => m.ClientTalentProcessDetailComponent
          ),
      },
      {
        path: 'talents',
        redirectTo: '/team-members',
        pathMatch: 'full',
      },
      // Legacy Talent detail → Team Member (Contact) detail.
      // `/talents/:talentId/processes/:processId` above stays for process bookmarks.
      {
        path: 'talents/:id',
        title: 'Team Member',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./talents/talent-to-team-member-redirect.component').then(
            m => m.TalentToTeamMemberRedirectComponent
          ),
      },
      {
        path: 'talents/:id/:tab',
        title: 'Team Member',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./talents/talent-to-team-member-redirect.component').then(
            m => m.TalentToTeamMemberRedirectComponent
          ),
      },
      {
        path: 'lead-generators',
        title: 'Lead Generators',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.LEAD_GENERATORS.LIST,
            PERMISSIONS.LEAD_GENERATORS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./lead-generators/lead-generator-list/lead-generator-list.component').then(
            m => m.LeadGeneratorListComponent
          ),
      },
      {
        path: 'lead-generators/:id',
        title: 'Lead Generator',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.LEAD_GENERATORS.LIST,
            PERMISSIONS.LEAD_GENERATORS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./lead-generators/lead-generator-detail/lead-generator-detail.component').then(
            m => m.LeadGeneratorDetailComponent
          ),
        children: [
          {
            path: 'commissions',
            loadComponent: () =>
              import('./lead-generators/lead-generator-detail/lead-generator-detail-tab-outlet.component').then(
                m => m.LeadGeneratorDetailTabOutletComponent
              ),
          },
          {
            path: 'clients',
            loadComponent: () =>
              import('./lead-generators/lead-generator-detail/lead-generator-detail-tab-outlet.component').then(
                m => m.LeadGeneratorDetailTabOutletComponent
              ),
          },
          {
            path: 'linkedin-activity',
            loadComponent: () =>
              import('./lead-generators/lead-generator-detail/lead-generator-detail-tab-outlet.component').then(
                m => m.LeadGeneratorDetailTabOutletComponent
              ),
          },
        ],
      },
      {
        path: 'recruiters/:id',
        title: 'Recruiter',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./recruiters/recruiter-detail/recruiter-detail.component').then(
            m => m.RecruiterDetailComponent
          ),
        children: [
          {
            path: 'candidates',
            loadComponent: () =>
              import('./recruiters/recruiter-detail/recruiter-detail-tab-outlet.component').then(
                m => m.RecruiterDetailTabOutletComponent
              ),
          },
          {
            path: 'placements',
            loadComponent: () =>
              import('./recruiters/recruiter-detail/recruiter-detail-tab-outlet.component').then(
                m => m.RecruiterDetailTabOutletComponent
              ),
          },
        ],
      },
      {
        path: 'recruiters',
        title: 'Recruiters',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./recruiters/recruiter-list/recruiter-list.component').then(
            m => m.RecruiterListComponent
          ),
      },
      {
        path: 'contracts',
        title: 'Contracts',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CONTRACTS.LIST,
            PERMISSIONS.CONTRACTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./contracts/contract-list/contract-list.component').then(
            m => m.ContractListComponent
          ),
      },
      {
        path: 'contracts/:id',
        title: 'Contract',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CONTRACTS.LIST,
            PERMISSIONS.CONTRACTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./contracts/contract-detail/contract-detail.component').then(
            m => m.ContractDetailComponent
          ),
      },
      {
        path: 'talent/hr/hhrr/interviews/:id',
        title: 'Interview feedback',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.HR.LIST,
            PERMISSIONS.HR.MANAGER,
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./compliance/compliance-interview-detail/compliance-interview-detail.component').then(
            m => m.ComplianceInterviewDetailComponent
          ),
      },
      {
        path: 'talent/hr',
        title: 'Talent HR',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          // HR Ops. Only hr:*. Compliance keys do not enter.
          permissions: [PERMISSIONS.HR.LIST, PERMISSIONS.HR.MANAGER, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadChildren: () => import('./talent-hr/talent-hr.routes').then(m => m.default),
      },
      // Legacy Compliance HR URLs → Talent HR (before loadChildren catch-all).
      { path: 'legal/compliance/hhrr', redirectTo: '/talent/hr/hhrr', pathMatch: 'full' },
      { path: 'legal/compliance/org-chart', redirectTo: '/talent/hr/org-chart', pathMatch: 'full' },
      {
        path: 'legal/compliance/performance-reviews',
        redirectTo: '/talent/hr/performance-reviews',
        pathMatch: 'full',
      },
      { path: 'legal/compliance/training', redirectTo: '/talent/hr/training', pathMatch: 'full' },
      {
        path: 'legal/compliance/whistleblow',
        redirectTo: '/talent/hr/whistleblow',
        pathMatch: 'full',
      },
      {
        path: 'legal/compliance/access-tickets',
        redirectTo: '/talent/hr/access-tickets',
        pathMatch: 'full',
      },
      {
        path: 'legal/compliance',
        title: 'Compliance',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.COMPLIANCE.LIST,
            PERMISSIONS.COMPLIANCE.AUDITOR_VIEW,
            PERMISSIONS.COMPLIANCE.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadChildren: () => import('./compliance/compliance.routes').then(m => m.default),
      },
      {
        path: 'platform-ops/security',
        title: 'Security Dashboard',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SECURITY_OPS.LIST,
            PERMISSIONS.SECURITY_OPS.MANAGE,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./platform-ops/security/security-ops-dashboard.component').then(
            m => m.SecurityOpsDashboardComponent
          ),
      },
      {
        path: 'platform-ops/findings',
        title: 'Security Findings',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SECURITY_OPS.LIST,
            PERMISSIONS.SECURITY_OPS.MANAGE,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./platform-ops/security/security-center.component').then(
            m => m.SecurityCenterComponent
          ),
      },
      {
        path: 'platform-ops/access',
        redirectTo: '/users',
        pathMatch: 'full',
      },
      {
        path: 'platform-ops/events',
        title: 'Security events',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SECURITY_OPS.LIST,
            PERMISSIONS.SECURITY_OPS.MANAGE,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./compliance/compliance-security-events/compliance-security-events.component').then(
            m => m.ComplianceSecurityEventsComponent
          ),
      },
      {
        path: 'platform-ops/incidents',
        title: 'Incidents',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SECURITY_OPS.LIST,
            PERMISSIONS.SECURITY_OPS.MANAGE,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./platform-ops/incidents/incidents-center.component').then(
            m => m.IncidentsCenterComponent
          ),
      },
      {
        path: 'platform-ops/dlp',
        title: 'DLP',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SECURITY_OPS.LIST,
            PERMISSIONS.SECURITY_OPS.MANAGE,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./platform-ops/dlp/dlp-center.component').then(m => m.DlpCenterComponent),
      },
      {
        path: 'platform-ops/oauth',
        title: 'Connected apps',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CONNECTED_APPS.LIST,
            PERMISSIONS.CONNECTED_APPS.MANAGE,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./platform-ops/oauth/oauth-connections.component').then(
            m => m.OauthConnectionsComponent
          ),
      },
      {
        path: 'platform-ops/audit',
        title: 'Audit Center',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.AUDIT_OPS.LIST, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadComponent: () =>
          import('./platform-ops/audit/audit-center.component').then(m => m.AuditCenterComponent),
      },
      {
        path: 'platform-ops/changelog',
        title: 'Changelog',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.CHANGELOG_OPS.LIST, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadComponent: () =>
          import('./platform-ops/changelog/changelog-center.component').then(
            m => m.ChangelogCenterComponent
          ),
      },
      {
        path: 'platform-ops/changelog/:id',
        title: 'Release',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.CHANGELOG_OPS.LIST, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadComponent: () =>
          import('./platform-ops/changelog/changelog-release-detail.component').then(
            m => m.ChangelogReleaseDetailComponent
          ),
      },
      {
        path: 'milestones',
        title: 'Milestones',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.MILESTONES.VIEW_ALL,
            PERMISSIONS.MILESTONES.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./milestones/all-milestones/all-milestones.component').then(
            m => m.AllMilestonesComponent
          ),
      },
      {
        path: 'assignments/:id',
        title: 'Assignment',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.ASSIGNMENTS.LIST,
            PERMISSIONS.ASSIGNMENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./assignments/assignment-detail/assignment-detail.component').then(
            m => m.AssignmentDetailComponent
          ),
      },
      {
        path: 'assignments',
        title: 'Assignments',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.ASSIGNMENTS.LIST,
            PERMISSIONS.ASSIGNMENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./assignments/assignment-list/assignment-list.component').then(
            m => m.AssignmentListComponent
          ),
      },
      {
        path: 'timesheets',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TIMESHEETS.LIST,
            PERMISSIONS.TIMESHEETS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./timesheets/timesheet-admin/timesheet-admin.component').then(
            m => m.TimesheetAdminComponent
          ),
        title: 'Timesheet Administration',
      },
      {
        path: 'finance/invoices',
        title: 'Invoices',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.FINANCIAL.VIEW_REVENUE,
            PERMISSIONS.FINANCIAL.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadChildren: () => import('./invoices/invoices.routes').then(m => m.invoicesRoutes),
      },
      {
        path: 'finance/commissions',
        title: 'Commissions',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.COMMISSIONS.LIST,
            PERMISSIONS.COMMISSIONS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadChildren: () => import('./commissions/commissions.routes').then(m => m.default),
      },
      {
        path: 'finance/bonus-claims',
        title: 'Bonus payment',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.PAYMENTS.LIST,
            PERMISSIONS.PAYMENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./bonus-claims/bonus-claims-list/bonus-claims-list.component').then(
            m => m.BonusClaimsListComponent
          ),
      },
      {
        path: 'finance/payments',
        title: 'Payments',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.PAYMENTS.LIST,
            PERMISSIONS.PAYMENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./payments/payment-list/payment-list.component').then(
            m => m.PaymentListComponent
          ),
      },
      {
        path: 'finance/payments/new',
        title: 'New Payment',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.PAYMENTS.CREATE,
            PERMISSIONS.PAYMENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./payments/payment-form/payment-form.component').then(
            m => m.PaymentFormComponent
          ),
      },
      {
        path: 'finance/payments/:id',
        title: 'Payment',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.PAYMENTS.LIST,
            PERMISSIONS.PAYMENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./payments/payment-detail/payment-detail.component').then(
            m => m.PaymentDetailComponent
          ),
      },
      {
        path: 'finance/payments/:id/edit',
        title: 'Edit Payment',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.PAYMENTS.UPDATE,
            PERMISSIONS.PAYMENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./payments/payment-form/payment-form.component').then(
            m => m.PaymentFormComponent
          ),
      },
      {
        path: 'invoices/new',
        redirectTo: '/finance/invoices/new',
        pathMatch: 'full',
      },
      {
        path: 'invoices/:id/edit',
        redirectTo: '/finance/invoices/:id/edit',
      },
      {
        path: 'invoices/:id',
        redirectTo: '/finance/invoices/:id',
      },
      {
        path: 'invoices',
        redirectTo: '/finance/invoices',
        pathMatch: 'full',
      },
      {
        path: 'commissions/settings',
        redirectTo: '/settings/commissions',
        pathMatch: 'full',
      },
      {
        path: 'commissions/:id/edit',
        redirectTo: '/finance/commissions/:id',
      },
      {
        path: 'commissions/:id',
        redirectTo: '/finance/commissions/:id',
      },
      {
        path: 'commissions',
        redirectTo: '/finance/commissions',
        pathMatch: 'full',
      },
      {
        path: 'payments/new',
        redirectTo: '/finance/payments/new',
        pathMatch: 'full',
      },
      {
        path: 'payments/:id/edit',
        redirectTo: '/finance/payments/:id/edit',
      },
      {
        path: 'payments/:id',
        redirectTo: '/finance/payments/:id',
      },
      {
        path: 'payments',
        redirectTo: '/finance/payments',
        pathMatch: 'full',
      },
      {
        path: 'invoice-config',
        redirectTo: '/settings/invoice-config',
        pathMatch: 'full',
      },
      {
        path: 'cv-config',
        redirectTo: '/settings/cv-config',
        pathMatch: 'full',
      },
      {
        path: 'team-members/cv-preview/:talentId',
        title: 'CV Preview',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TALENTS.LIST,
            PERMISSIONS.TALENTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./talents/cv-preview/cv-preview.component').then(m => m.CvPreviewComponent),
      },
      {
        path: 'settings',
        title: 'Settings',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SETTINGS.VIEW,
            PERMISSIONS.SETTINGS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      ...SETTINGS_GROUPS.filter(group => group.landingSegment).map(group => ({
        path: `settings/${group.landingSegment}`,
        title: group.label,
        canActivate: [AuthGuard, PermissionGuard],
        data: { permissions: settingsGroupPermissions(group) },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      })),
      {
        path: 'settings/roles',
        title: 'Roles',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.ROLES.LIST,
            PERMISSIONS.ROLES.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/technologies',
        title: 'Technologies',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.TECHNOLOGIES.LIST,
            PERMISSIONS.TECHNOLOGIES.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/specialities',
        title: 'Specialities',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SPECIALITIES.LIST,
            PERMISSIONS.SPECIALITIES.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/processes',
        title: 'Processes',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.PROCESSES.LIST,
            PERMISSIONS.PROCESSES.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/commissions',
        title: 'Commissions',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.COMMISSIONS.VIEW_SETTINGS,
            PERMISSIONS.COMMISSIONS.MANAGE_SETTINGS,
            PERMISSIONS.COMMISSIONS.UPDATE_CONFIG,
            PERMISSIONS.COMMISSIONS.VIEW_CONFIG,
            PERMISSIONS.COMMISSIONS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/cv-config',
        title: 'CV Configuration',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.TALENTS.MANAGER, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/bonus-buffer',
        title: 'Bonus Payment Buffer',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.TIMESHEETS.MANAGER, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/invoice-config',
        title: 'Invoice Configuration',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.FINANCIAL.MANAGER, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/notifications',
        title: 'Notifications',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.NOTIFICATION_HUB.VIEW,
            PERMISSIONS.NOTIFICATION_HUB.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/support-tickets',
        title: 'Support Tickets',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SUPPORT_TICKETS.LIST,
            PERMISSIONS.SUPPORT_TICKETS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/ai-governance',
        title: 'AI Governance',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.SETTINGS.VIEW,
            PERMISSIONS.SETTINGS.MANAGER,
            PERMISSIONS.SETTINGS.UPDATE,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/ai',
        redirectTo: '/settings/ai-governance',
        pathMatch: 'full',
      },
      {
        path: 'settings/esign',
        title: 'Signatures',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.COMPLIANCE.MANAGER, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/approvers',
        title: 'Approvers',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.COMPLIANCE.MANAGER, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/controls',
        title: 'Controls',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [...COMPLIANCE_CONTROL_MANAGE_PERMISSIONS],
        },
        loadComponent: () => import('./settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'settings/signatures',
        redirectTo: '/settings/esign',
        pathMatch: 'full',
      },
      {
        path: 'relay',
        title: 'Relay',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.RELAY.ADMIN, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadChildren: () => import('./relay-admin/relay-admin.routes').then(m => m.default),
      },
      {
        path: 'signals',
        title: 'Signals',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [PERMISSIONS.SIGNALS.ADMIN, PERMISSIONS.GLOBAL.ADMIN],
        },
        loadChildren: () => import('./signals-admin/signals-admin.routes').then(m => m.default),
      },
      {
        path: 'users',
        title: 'Users',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.USERS.LIST,
            PERMISSIONS.USERS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./users/user-list/user-list.component').then(m => m.UserListComponent),
      },
      {
        path: 'contacts',
        title: 'Contacts',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CONTACTS.LIST,
            PERMISSIONS.CONTACTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./contacts/contact-list/contact-list.component').then(
            m => m.ContactListComponent
          ),
      },
      {
        path: 'gifts',
        title: 'Gifts',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CONTACTS.LIST,
            PERMISSIONS.CONTACTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./gifts/gift-list/gift-list.component').then(m => m.GiftListComponent),
      },
      {
        path: 'contacts/:id',
        title: 'Contact',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.CONTACTS.LIST,
            PERMISSIONS.CONTACTS.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./contacts/contact-detail/contact-detail.component').then(
            m => m.ContactDetailComponent
          ),
        children: personProfileTabRoutes(),
      },
      {
        path: 'login',
        title: 'Sign In',
        loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent),
        canActivate: [LoginGuard],
      },
      {
        path: 'oauth/connect',
        title: 'Connect application',
        loadComponent: () =>
          import('./auth/oauth-connect/oauth-connect.component').then(m => m.OAuthConnectComponent),
      },
      {
        path: 'forgot-password',
        title: 'Forgot Password',
        loadComponent: () =>
          import('./auth/forgot-password/forgot-password.component').then(
            m => m.ForgotPasswordComponent
          ),
        canActivate: [LoginGuard],
      },
      {
        path: 'reset-password',
        title: 'Reset Password',
        loadComponent: () =>
          import('./auth/reset-password/reset-password.component').then(
            m => m.ResetPasswordComponent
          ),
        canActivate: [LoginGuard],
      },
      {
        path: 'profile/approvals',
        title: 'Approvals',
        canActivate: [AuthGuard, PermissionGuard],
        data: {
          permissions: [
            PERMISSIONS.COMPLIANCE.LIST,
            PERMISSIONS.COMPLIANCE.AUDITOR_VIEW,
            PERMISSIONS.COMPLIANCE.MANAGER,
            PERMISSIONS.GLOBAL.ADMIN,
          ],
        },
        loadComponent: () =>
          import('./compliance/compliance-approvals/compliance-approvals.component').then(
            m => m.ComplianceApprovalsComponent
          ),
      },
      {
        path: 'profile',
        title: 'Profile',
        canActivate: [AuthGuard, PermissionGuard],
        data: { allowAuthenticated: true },
        loadComponent: () => import('./profile/profile.component').then(m => m.ProfileComponent),
      },
      {
        path: '**',
        title: 'Not Found',
        loadComponent: () =>
          import('./not-found/not-found.component').then(m => m.NotFoundComponent),
      },
    ],
  },
];
